import * as React from 'react';
import { cn } from '@/lib/utils/cn';

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
type AvatarStatus = 'online' | 'offline' | 'away' | 'busy';

export interface AvatarProps {
  /** Initials shown when there's no image (or while it loads / on error).
   *  `initials` is the canonical name; `fallback` is kept as an alias for the
   *  many existing call sites that use it. */
  fallback?: string;
  initials?: string;
  src?: string;
  alt?: string;
  size?: AvatarSize;
  /** Presence dot. `status` is canonical; `presence` is an alias kept for the
   *  call sites migrated from the old KitAvatar. */
  status?: AvatarStatus;
  presence?: AvatarStatus;
  /** Solid color or gradient string for the circle (defaults to brand grad). */
  color?: string;
  /** Rounded-square instead of a full circle. */
  square?: boolean;
  /** Wrap in the gradient presence ring. */
  ring?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Canonical Blaze avatar — renders BlazeUI `bzc-avatar` markup. Initials always
 * render on the colored circle; the <img> overlays only once it actually loads,
 * so broken/blocked URLs degrade to initials instead of a blank circle.
 */
const Avatar = ({
  fallback,
  initials,
  src,
  alt,
  size = 'md',
  status,
  presence,
  color,
  square,
  ring,
  className,
  style,
}: AvatarProps) => {
  const label = (initials ?? fallback ?? '').toString();
  const dot = status ?? presence;
  const [imgFailed, setImgFailed] = React.useState(false);

  // Reset the failed flag when the URL changes (different user/conversation).
  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setImgFailed(false);
  }, [src]);

  const showImg = !!src && !imgFailed;

  const node = (
    <div
      className={cn('bzc-avatar', `bzc-avatar--${size}`, square && 'bzc-avatar--square', className)}
      style={color ? { background: color, ...style } : style}
    >
      <span aria-hidden style={{ userSelect: 'none' }}>
        {label.slice(0, 2).toUpperCase()}
      </span>
      {showImg && <img src={src} alt={alt ?? ''} onError={() => setImgFailed(true)} />}
      {dot && (
        <span className={cn('bzc-avatar__status', dot !== 'online' && `bzc-avatar__status--${dot}`)} />
      )}
    </div>
  );

  return ring ? <span className="bzc-avatar__ring">{node}</span> : node;
};

export { Avatar };
